import { lessonSelectors, lessonTaskSelectors } from 'common/modules/ReactShared';
import { t, Translate } from 'i18n';
import { FC, useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useTypedSelector } from 'students/stores/_utils';
import ArrowRightIcon from 'students/views/shared/assets/icons/arrow_right_icon.svg';
import { styleInnerButton } from 'students/views/shared/components/ButtonGeneral/ButtonGeneral';
import { Spinner } from 'students/views/shared/components/Spinner';
import UrlIcon from 'students/views/shared/components/UrlIcon';
import { getBackDestinationUrl } from 'students/views/shared/helpers';
import { customMediaQuery, onLightTheme } from 'students/views/shared/styled';
import { SPrimaryButton, STransparentButton } from 'students/views/shared/styled/SButton';
import styled from 'styled-components';
import { useNavFooterIsDarkFont } from '../hooks/useNavFooterIsDarkFont';

interface IProps {
  onNext?: () => void;
  onSkip?: () => void;
  isCompleting: boolean;
  isLessonCompleted?: boolean;
  previousTaskId?: number | null;
}
const CheckNavFooter: FC<IProps> = ({
  onNext,
  onSkip,
  isCompleting,
  isLessonCompleted,
  previousTaskId
}) => {
  const [nextBtnCompleting, setNextBtnCompleting] = useState<boolean>(false);
  const [skipBtnCompleting, setSkipBtnCompleting] = useState<boolean>(false);
  const taskSession = useTypedSelector(lessonTaskSelectors.selectLessonTaskSession);
  const lessonSession = useTypedSelector(lessonSelectors.selectLessonSession);
  const history = useHistory();
  const isDarkFont = useNavFooterIsDarkFont();

  useEffect(() => {
    if (!isCompleting) {
      setNextBtnCompleting(false);
      setSkipBtnCompleting(false);
    }
  }, [isCompleting]);

  const handleNext = () => {
    onNext?.();
    setNextBtnCompleting(true);
  };

  const handleSkip = () => {
    onSkip?.();
    setSkipBtnCompleting(true);
  };

  const handlePrevious = useCallback(() => {
    const destination = getBackDestinationUrl(
      taskSession,
      lessonSession,
      window.location.pathname
    );
    if (destination) history.push(destination);
  }, [history, taskSession, lessonSession]);

  const renderNextBtnContent = () =>
    nextBtnCompleting ? (
      <Spinner />
    ) : (
      <>
        <Translate str="frontend.lesson_task.check.navigation_next" />

        <SIcon
          className="dir-auto"
          url={ArrowRightIcon}
          height="1.5rem"
          width="1.5rem"
          color="#FFFFFF"
        />
      </>
    );

  const renderSkipBtnContent = () =>
    skipBtnCompleting ? (
      <Spinner />
    ) : (
      <Translate str="frontend.lesson_task.check.navigation_skip" />
    );

  return (
    <SCheckNavFooterWrapper>
      {isLessonCompleted ? (
        <SText
          style={{
            color: isDarkFont ? 'var(--color-black)' : 'var(--ln-ink-on-dark)'
          }}
        >
          <Translate str="frontend.lesson_task.check.restart_the_lesson_to_submit_new_answer" />
        </SText>
      ) : null}

      <SCheckNavFooterButtons>
        {previousTaskId ? (
          <SButtonWrapper className="ln-mr-auto">
            <SPreviousBtn
              data-testid="students/components/TaskSubjects/Check/NavFooter/PreviousButton"
              disabled={isCompleting}
              onClick={handlePrevious}
              className="ln-transition-colors ln-duration-300"
            >
              <SPreviousIcon
                url={ArrowRightIcon}
                color="#FFFFFF"
                width="1.5rem"
                height="1.5rem"
              />
              <span>
                {t('frontend.lesson_task.check.navigation_previous', {
                  defaultValue: 'Previous'
                })}
              </span>
            </SPreviousBtn>
          </SButtonWrapper>
        ) : null}

        {onSkip ? (
          <SButtonWrapper>
            <SSecondaryBtn
              $isDarkFont={isDarkFont}
              data-testid="students/components/TaskSubjects/Check/NavFooter/SkipButton"
              disabled={isCompleting}
              onClick={handleSkip}
              className="ln-transition-colors ln-duration-300"
            >
              {renderSkipBtnContent()}
            </SSecondaryBtn>
          </SButtonWrapper>
        ) : null}

        {onNext ? (
          <SButtonWrapper>
            <SPrimaryBtn
              data-testid="students/components/TaskSubjects/Check/NavFooter/NextButton"
              disabled={isCompleting || isLessonCompleted}
              onClick={handleNext}
              className="!ln-bg-blue hover:!ln-bg-blue/70 ln-transition-colors ln-duration-300"
            >
              {renderNextBtnContent()}
            </SPrimaryBtn>
          </SButtonWrapper>
        ) : null}
      </SCheckNavFooterButtons>
    </SCheckNavFooterWrapper>
  );
};

export default CheckNavFooter;

const SCheckNavFooterWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const SText = styled.span`
  text-align: center;
`;

export const SCheckNavFooterButtons = styled.div`
  display: flex;
  justify-content: center;
  gap: 15px;
  width: 100%;
  max-width: 748px;

  ${customMediaQuery('tablet')} {
    gap: 32px;
  }
`;

export const SPrimaryBtn = styled(SPrimaryButton)`
  display: flex;
  justify-content: center;
  align-items: center;
  box-shadow: 0 0 16px rgba(0, 0, 0, 0.1);
  color: var(--color-white);
  border-radius: 20px;

  ${styleInnerButton()} {
    padding: 0 2rem;
  }
`;

const SPreviousBtn = styled(STransparentButton)`
  font-weight: 400;
  font-size: 18px !important;
  border-radius: 20px;
  opacity: 0.8;

  ${customMediaQuery('mobile')} {
    & .button-inner {
      padding-left: 0.5rem;
      padding-right: 0.5rem;

      & > div {
        margin: 0;
      }

      & > span {
        display: none;
      }
    }
  }
`;

export const SSecondaryBtn = styled(STransparentButton)<{ $isDarkFont: boolean }>`
  ${({ $isDarkFont }) => onLightTheme`
    color: ${$isDarkFont ? 'var(--color-black)' : 'var(--ln-ink-on-dark)'};
  `}

  font-weight: 400;
  font-size: 18px !important;
  border-radius: 20px;
  opacity: 0.8;

  ${customMediaQuery('mobile')} {
    font-size: 14px !important;
  }

  ${styleInnerButton()} {
    padding: 0 2rem;
  }
`;

export const SButtonWrapper = styled.div`
  ${customMediaQuery('mobile')} {
    min-width: 0;

    & > * {
      max-width: 100%;
    }
  }

  & > * {
    font-size: 0.875rem;
    min-height: 3rem;

    ${customMediaQuery('tablet')} {
      font-size: 1.375rem;
      min-height: 3.75rem;
      width: 100%;
      min-width: 180px;
    }
  }
`;

export const SIcon = styled(UrlIcon)`
  margin-inline-start: 0.75rem;
  margin-inline-end: -0.75rem;
`;

const SPreviousIcon = styled(UrlIcon)`
  margin-inline-start: -0.75rem;
  margin-inline-end: 0.75rem;
  transform: rotate(180deg);
`;
